import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const testimonials = [
  {
    quote: "For more than 30 years, IPMI has opened the world of precious metals to me. Through its relationships, education, and leadership opportunities—including the honor of serving as IPMI's 43rd President—it shaped not only my career, but my vocation. IPMI didn't just connect me to an industry; it gave me a place to serve.",
    author: "Becky Berube",
    title: "President & Co-Founder",
    company: "United Catalyst Corporation",
    subtitle: "IPMI Member since 1993",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/4hgp8gnk_Beck%20Berube%20headshot.webp"
  },
  {
    quote: "As IPMI celebrates its 50th Anniversary, I extend my sincere congratulations on this remarkable milestone and reflect with deep appreciation on its enduring leadership within the global precious metals community. Over the course of my long-standing engagement with LBMA, LPPM, IPA, and IPMI, IPMI has stood as a cornerstone of trust, professional excellence, and international collaborations. Since becoming a member in 2006, the relationships, knowledge exchange, and shared commitment fostered through IPMI have profoundly shaped my career and strengthened our organisation's long-standing global contribution, as Tanaka continues its legacy following its 140th anniversary last year and now enters its 141st year.",
    author: "Hitoshi Kosai",
    title: "Exclusive Advisor",
    company: "TANAKA PRECIOUS METAL GROUP Co.,Ltd / TANAKA PRECIOUS METAL TECHNOLOGIES Co.,Ltd",
    subtitle: "Member since 2006",
    image: "https://customer-assets.emergentagent.com/job_milestone-fifty-1/artifacts/u30xmuoi_Hitoshi-Kosai%402x-100.jpg"
  },
  {
    quote: "Being a member of IPMI has helped BASIQ to make invaluable connections in the industry, build a reputation, and grow our business. In parallel, many relationships developed and turned into friendships.",
    author: "Regine Albrecht",
    title: "President",
    company: "BASIQ Corporation",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/2ce5kcgq_2016%20Regine%20headshot.jpg"
  },
  {
    quote: "I joined IPMI in 2012 when I signed up to give a technical talk on Fire Assay; little did I know that this decision would shape the course of my career. Over the years, IPMI has given me opportunities I never imagined, serving as President (2022-2023), and as a member of the Board of Directors and Executive Committee. Through this organization, I've grown both professionally and personally, learning immeasurably from industry leaders and peers. More than that, I've built lifelong friendships that I truly cherish. IPMI's commitment to excellence and collaboration has made it a cornerstone of our industry, and I'm proud to celebrate 50 years of innovation and community.",
    author: "Zachary J. Henneman, PhD",
    title: "Laboratory & Technical Director",
    company: "Ledoux & Co.",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/3mqpf1ni_Henneman%20Headshot.png"
  },
  {
    quote: "IPMI is at the forefront of the metals recycling industry, creating a unique forum for industry experts to learn new ideas and connect with peers. The relationships and insights have helped guide Sabin Metals Corp for the last 40+ years.",
    author: "Samuel Sabin",
    title: "Corporate Development",
    company: "Sabin Metals Corp",
    subtitle: "Member since 1980",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/ibfjldpt_Sam%20Sabin%20testimonial.jpg"
  },
  {
    quote: "IPMI has been a key contributor to my personal and professional development during my 25 years of membership. Through networking with individuals in the technical, commercial, and legal areas associated with precious metals, I was able to leverage the knowledge gained to great advantage in my workplace at BASF and Engelhard. This result was unique in my experience as a 40 year employee in the chemical / precious metals industries. It also was of great value during my second career as an educator at the undergraduate and graduate levels.",
    author: "Robert M. Ianniello, Ph.D.",
    title: "IPMI Member",
    subtitle: "25 Years of Membership",
    image: "https://customer-assets.emergentagent.com/job_milestone-fifty-1/artifacts/zjbai0am_Bob%20Ianniello.png"
  },
  {
    quote: "For me personally, IPMI has been one of the most reliable places to build long-term relationships in the precious metals markets. Over fifteen years of membership, Kitco has benefited from the connections, conversations, and collaboration that only a community like IPMI can bring together.",
    author: "John Dourekas",
    title: "Media and Institutional Sales Director",
    company: "Kitco",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/k3b01zwj_John%20D%20headshot.jpeg"
  }
];

const TestimonialsPage = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-50px" });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const subject = encodeURIComponent(`IPMI 50th Anniversary - Inquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Organization: ${formData.organization}\n\n` +
      `Message:\n${formData.message}`
    );
    
    window.location.href = `mailto:mail@ipmi.org?subject=${subject}&body=${body}`;
    
    setTimeout(() => {
      setIsSubmitting(false);
    }, 500);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="testimonials-page" ref={sectionRef}>
      {/* Hero Header */}
      <div className="testi-page-header">
        {/* 50th Emblem */}
        <a 
          href="https://www.ipmi.org/50th/ipmi-50th-logo-presentation" 
          target="_blank" 
          rel="noopener noreferrer"
          className="testi-page-logo-link"
        >
          <motion.img
            src="https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/o4ewfjxj_IPMI-50th-Emblem.png"
            alt="IPMI 50th Anniversary"
            className="testi-page-logo"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          />
        </a>
        
        <motion.h1
          className="testi-page-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Join the Next Chapter
        </motion.h1>
        
        <motion.p
          className="testi-page-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          As we celebrate 50 years, we're looking forward to the next 50. Whether you're a longtime member or exploring IPMI for the first time, we'd love to hear from you.
        </motion.p>
      </div>

      {/* Main Testimonials Section */}
      <div className="testi-page-main">
        {/* Decorative quote */}
        <div className="testi-page-quote-bg">"</div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="testi-page-carousel"
        >
          <div className="testi-page-card">
            <Quote className="testi-page-quote-icon" size={48} />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="testi-page-content"
              >
                {/* Headshot */}
                {currentTestimonial.image && (
                  <div className="testi-page-headshot">
                    <img 
                      src={currentTestimonial.image} 
                      alt={currentTestimonial.author}
                    />
                  </div>
                )}
                
                <blockquote className="testi-page-quote-text">
                  "{currentTestimonial.quote}"
                </blockquote>
                
                <div className="testi-page-attribution">
                  <span className="testi-page-author">— {currentTestimonial.author}</span>
                  <span className="testi-page-role">
                    {currentTestimonial.title}
                    {currentTestimonial.company && ` - ${currentTestimonial.company}`}
                  </span>
                  {currentTestimonial.subtitle && (
                    <span className="testi-page-member-since">{currentTestimonial.subtitle}</span>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <div className="testi-page-nav">
              <button 
                onClick={goToPrevious} 
                className="testi-page-arrow testi-page-arrow-prev"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                onClick={goToNext} 
                className="testi-page-arrow testi-page-arrow-next"
                aria-label="Next testimonial"
              >
                <ChevronRight size={28} />
              </button>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="testi-page-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`testi-page-dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>

        {/* CTA after testimonials */}
        <motion.div
          className="testi-carousel-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <a 
            href="https://www.ipmi.org/membership" 
            target="_blank" 
            rel="noopener noreferrer"
            className="testi-page-cta-btn"
          >
            View Membership Options
          </a>
        </motion.div>
      </div>

      {/* Member Benefits Section */}
      <motion.div
        className="testi-benefits-section"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <h2 className="testi-benefits-title">Member Benefits</h2>
        <div className="testi-benefits-grid">
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Unlimited networking opportunities</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Special Member pricing for all events</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Precious Metals Monthly Newsletter</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Members Directory</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">IPMI Buyers' Guide to Precious Metals Products and Services, published annually</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">The IPMI Journal</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Comprehensive technical reference library</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Members Only Website area containing valuable information for IPMI members</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Access to regional chapters events – Europe, Metro New York, New England, and Central States chapters</span>
          </div>
          <div className="testi-benefit-item">
            <span className="testi-benefit-icon">✦</span>
            <span className="testi-benefit-text">Recognition & Growth – Awards, leadership opportunities, and visibility that build credibility in the precious metals community</span>
          </div>
        </div>
        <a 
          href="https://www.ipmi.org/membership" 
          target="_blank" 
          rel="noopener noreferrer"
          className="testi-benefits-cta"
          data-testid="become-member-btn"
        >
          Become a Member
        </a>
      </motion.div>

      {/* CTA Section with Form */}
      <motion.div
        className="testi-page-cta"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <p className="testi-page-tagline">
          Connecting the precious metals community since 1976
        </p>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="testi-page-form">
          <h3 className="testi-form-heading">Get in Touch</h3>
          
          <div className="testi-form-row">
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="testi-form-input"
            />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="testi-form-input"
            />
          </div>
          
          <input
            type="text"
            name="organization"
            placeholder="Organization (Optional)"
            value={formData.organization}
            onChange={handleChange}
            className="testi-form-input"
          />
          
          <textarea
            name="message"
            placeholder="How can we help?"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            className="testi-form-textarea"
          />
          
          <button type="submit" className="testi-form-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : <>Send Message <Send size={18} /></>}
          </button>
        </form>

        {/* Social Links */}
        <div className="testi-social-section">
          <p className="testi-social-label">Connect With Us</p>
          <div className="testi-social-links">
            <a href="https://www.linkedin.com/company/international-precious-metals-institute-ipmi-/" target="_blank" rel="noopener noreferrer" className="testi-social-link" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://www.youtube.com/@TheIPMI" target="_blank" rel="noopener noreferrer" className="testi-social-link" aria-label="YouTube">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a href="https://x.com/preciousipmi?lang=en" target="_blank" rel="noopener noreferrer" className="testi-social-link" aria-label="X (Twitter)">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/people/International-Precious-Metals-Institute-IPMI/100060316915226/" target="_blank" rel="noopener noreferrer" className="testi-social-link" aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TestimonialsPage;
